import type firebase from 'firebase/app';
import invariant from 'tiny-invariant';

import type { Structure } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import {
  batchMarkVersionsAsNotCurrent,
  getOrCreateModelDocument,
} from 'src/modules/designStudio/machines/firebaseUtils';
import type { LegacyDesignConfig } from 'src/modules/designStudio/types';
import { withFirebaseErrorSentry } from 'src/shared/telemetry/sentry';

import type { ShareDocument, VersionDocument } from './documentTypes';
import { FirebaseClient } from './firebaseClient';

class FirestoreEngine {
  ref?: firebase.firestore.DocumentReference;

  jobId?: number;

  constructor(jobId: number) {
    this.jobId = jobId;
  }

  async getBuildingModelReference() {
    invariant(this.jobId, 'Missing jobId from BuildingModel');
    if (!this.ref) {
      const querySnapshot = (await getOrCreateModelDocument(this.jobId))
        .docs[0];
      this.ref = querySnapshot.ref;
    }
    return this.ref;
  }

  async getShare(shareToken: string) {
    const ref = await this.getBuildingModelReference();
    const shareRef = await withFirebaseErrorSentry(() =>
      ref.collection('shares').doc(shareToken).get(),
    )();
    if (!shareRef) {
      return undefined;
    }
    return shareRef.data() as ShareDocument;
  }

  async getVersionMarkedAsCurrent(userId: number) {
    const ref = await this.getBuildingModelReference();
    return withFirebaseErrorSentry(() =>
      ref
        .collection('versions')
        .where('author_id', '==', userId)
        .where('current', '==', true)
        .orderBy('created_at')
        .limitToLast(1)
        .get(),
    )();
  }

  async getVersionsToBeDeleted(
    createdAt: Date,
    userId: number,
    sessionId: string,
  ) {
    const ref = await this.getBuildingModelReference();
    return withFirebaseErrorSentry(() =>
      ref
        .collection('versions')
        .where('author_id', '==', userId)
        .where('session_id', '==', sessionId)
        .where('created_at', '>', createdAt)
        .get(),
    )();
  }

  async createVersion(
    authorId: number,
    sessionId: string,
    history: VersionDocument[] | null,
    designConfig: LegacyDesignConfig | Structure,
    currentIndexInHistory: number | undefined,
  ) {
    const batch = batchMarkVersionsAsNotCurrent(history);
    const newVersionRef = this.ref?.collection('versions').doc();

    if (newVersionRef) {
      batch.set(newVersionRef, {
        author_id: authorId,
        created_at: FirebaseClient.getNowTimestamp(),
        design_config: designConfig,
        session_id: sessionId,
        current: true,
      });
    }

    if (
      currentIndexInHistory !== undefined &&
      history &&
      currentIndexInHistory !== history.length - 1
    ) {
      const currentVersion = history[currentIndexInHistory];

      let versionsToBeDeleted;

      if (currentVersion) {
        versionsToBeDeleted = await this.getVersionsToBeDeleted(
          currentVersion.created_at,
          authorId,
          sessionId,
        );
      }

      versionsToBeDeleted?.docs.forEach(versionToDelete =>
        batch.delete(versionToDelete.ref),
      );
    }

    return withFirebaseErrorSentry(() => batch.commit())();
  }
}

export { FirestoreEngine };
